import React, { useEffect, useRef, useState } from 'react'
import styles from './Calendar.module.css'
import { Up } from '../Up'
import { useTranslation } from 'next-i18next'

/** Each day in the calendar (prop shape). */
export interface DayProps {
  /** A real JS Date object. */
  date: Date
  /** e.g. 'RESERVATION', 'UNAVAILABILITY', 'ALL_SERVICES_MAIN_EVENT_AVAILABILITY' */
  type?: string
  /** True if this date is the start of a consecutive block of same-type days. */
  isStart: boolean
  /** True if this date is the end of a consecutive block of same-type days. */
  isEnd: boolean
  /** How many reservations occur on this date (for the “pastille” badge). */
  count?: number
}

/** Calendar component props. */
interface CalendarProps {
  weeksPerRow?: number
  viewOnly?: boolean
  currentMonth: Date
  selectedRange?: {
    start: Date | null
    end: Date | null
  }
  hoveredDate: Date | null
  /** (Optional) Days before this one can't be selected. */
  minDate?: Date
  /** (Optional) Days after this one can't be selected. */
  maxDate?: Date
  /** (Optional) Without `minDate`, days before today can't be selected. Defaults to true. */
  disablePast?: boolean
  /** (Optional) Month and year in the title open a list to pick them from. */
  monthYearPicker?: boolean

  /** Reservation days, each with optional `count` to indicate how many reservations. */
  reservationDays?: DayProps[]
  /** (Optional) For applying special styling to days that are "main event" days. */
  mainEventDays?: DayProps[]
  /** (Optional) For applying special styling to days that are "available" days. */
  availableDays?: DayProps[]
  /** (Optional) For applying special styling to days that are "unavailable" days. */
  unavailableDays?: DayProps[]

  setCurrentMonth: (date: Date) => void
  handleDateClick?: (day: number, monthOffset?: number) => void
  handleDateHover?: (date: Date | null) => void
}

/**
 * A reusable Calendar component that highlights various day types and
 * can optionally show a "pastille" (badge) if `count >= 2` for a day.
 */
export const Calendar: React.FC<CalendarProps> = ({
  weeksPerRow = 1,
  viewOnly = false,
  currentMonth,
  selectedRange,
  hoveredDate,
  minDate,
  maxDate,
  disablePast = true,
  monthYearPicker = false,

  reservationDays = [],
  mainEventDays = [],
  availableDays = [],
  unavailableDays = [],

  setCurrentMonth,
  handleDateClick,
  handleDateHover,
}) => {
  const { t } = useTranslation('common')
  // Days of week in French short labels
  const daysOfWeek: string[] = [
    t('days.reduced.monday'),
    t('days.reduced.tuesday'),
    t('days.reduced.wednesday'),
    t('days.reduced.thursday'),
    t('days.reduced.friday'),
    t('days.reduced.saturday'),
    t('days.reduced.sunday'),
  ]

  // Utility: get # of days in a given month of a given year
  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate()
  }

  /** Move the calendar by `months` (negative to go back). */
  const shiftMonth = (months: number): void => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + months, 1))
  }

  /** Days grid, or the month/year list opened from the title. */
  const [view, setView] = useState<'days' | 'months' | 'years'>('days')
  const toggleView = (list: 'months' | 'years') => setView((prev) => (prev === list ? 'days' : list))

  // Scrolls the opened list to the current month/year
  const pickerListRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const list = pickerListRef.current
    const selected = list?.querySelector<HTMLElement>('[data-selected="true"]')
    if (list && selected) {
      list.scrollTop = selected.offsetTop - (list.clientHeight - selected.clientHeight) / 2
    }
  }, [view])

  /** Returns a date with time set to 0:00 for comparison. */
  const normalizeDate = (date: Date): Date => {
    const normalized = new Date(date)
    normalized.setHours(0, 0, 0, 0)
    return normalized
  }

  /**
   * Check if `date` is in [start, end].
   * If start>end, it will invert automatically.
   */
  const isInRange = (date: Date, start: Date | null, end: Date | null): boolean => {
    if (!start || !end) return false
    const d = normalizeDate(date)
    const s = normalizeDate(start)
    const e = normalizeDate(end)
    return s <= e ? d >= s && d <= e : d >= e && d <= s
  }

  /** Combine day arrays into findable structures. */
  const getReservationDay = (date: Date) =>
    reservationDays.find((rd) => normalizeDate(rd.date).getTime() === normalizeDate(date).getTime())
  const getMainEventDay = (date: Date) =>
    mainEventDays.find((md) => normalizeDate(md.date).getTime() === normalizeDate(date).getTime())
  const getAvailableDay = (date: Date) =>
    availableDays.find((ad) => normalizeDate(ad.date).getTime() === normalizeDate(date).getTime())
  const getUnavailableDay = (date: Date) =>
    unavailableDays.find((ud) => normalizeDate(ud.date).getTime() === normalizeDate(date).getTime())

  // Check if a day belongs to each special type
  const isReservationDay = (date: Date) => !!getReservationDay(date)
  const isMainEventDay = (date: Date) => !!getMainEventDay(date)
  const isAvailableDay = (date: Date) => !!getAvailableDay(date)
  const isUnavailableDay = (date: Date) => !!getUnavailableDay(date)

  // Edges for consecutive blocks
  const isFirstReservationDay = (date: Date) => getReservationDay(date)?.isStart
  const isLastReservationDay = (date: Date) => getReservationDay(date)?.isEnd
  const isFirstMainEventDay = (date: Date) => getMainEventDay(date)?.isStart
  const isLastMainEventDay = (date: Date) => getMainEventDay(date)?.isEnd
  const isFirstAvailableDay = (date: Date) => getAvailableDay(date)?.isStart
  const isLastAvailableDay = (date: Date) => getAvailableDay(date)?.isEnd
  const isFirstUnavailableDay = (date: Date) => getUnavailableDay(date)?.isStart
  const isLastUnavailableDay = (date: Date) => getUnavailableDay(date)?.isEnd

  /**
   * If user selected a start date but not an end date, and hovers over day X,
   * we highlight the range from start -> hovered.
   */
  const hoveredRangeStart = selectedRange?.start && !selectedRange.end && hoveredDate ? selectedRange.start : null
  const hoveredRangeEnd = selectedRange?.start && !selectedRange.end && hoveredDate ? hoveredDate : null
  const isInHoveredRange = (date: Date) => {
    if (!hoveredRangeStart || !hoveredRangeEnd) return false
    return isInRange(date, hoveredRangeStart, hoveredRangeEnd)
  }

  /** Past/future limiting: e.g. disallow selection for past dates. */
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const isBeforeMin = (date: Date) => (minDate ? date < normalizeDate(minDate) : disablePast && date < yesterday)
  const isAfterMax = (date: Date) => !!maxDate && normalizeDate(date) > normalizeDate(maxDate)
  const isDisabledDate = (date: Date) => isBeforeMin(date) || isAfterMax(date)

  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()

  // Years offered by the year list
  const firstYear = minDate?.getFullYear() ?? (disablePast ? today.getFullYear() : today.getFullYear() - 100)
  const lastYear = maxDate?.getFullYear() ?? today.getFullYear() + 10

  const formatMonth = (monthIndex: number): string =>
    new Date(year, monthIndex, 1)
      .toLocaleString(t('language') as string, { month: 'long' })
      .replace(/^\w/, (c) => c.toUpperCase())
  const daysInMonth = getDaysInMonth(month, year)

  // Calculate offset for the first day in the month (Mon=0, Tue=1, etc.)
  const firstDayOfMonth = new Date(year, month, 1).getDay()
  const firstDayOffset = (firstDayOfMonth + 6) % 7 // Shift so Monday=0, Sunday=6
  const daysInPrevMonth = getDaysInMonth(month - 1, year)
  const hasFullSelectedRange = selectedRange?.start && selectedRange?.end

  /**
   * For styling the very first/last day in the user’s selected range.
   */
  const isFirstSelectedDay = (date: Date) => {
    if (!selectedRange?.start) return false
    return normalizeDate(date).getTime() === normalizeDate(selectedRange.start).getTime()
  }
  const isLastSelectedDay = (date: Date) => {
    if (!selectedRange?.start || !selectedRange.end) return false
    return normalizeDate(date).getTime() === normalizeDate(selectedRange.end).getTime()
  }

  /** Build the class names for each day cell. */
  const buildClassNames = (currentDate: Date, isOtherMonth: boolean) => {
    const isDisabled = isDisabledDate(currentDate)
    const inSelectedRange =
      hasFullSelectedRange && isInRange(currentDate, selectedRange?.start ?? null, selectedRange?.end ?? null)
    const inHoveredRange = !hasFullSelectedRange && isInHoveredRange(currentDate)

    // Check day “types”:
    const resDay = isReservationDay(currentDate)
    const mainEvent = isMainEventDay(currentDate)
    const available = isAvailableDay(currentDate)
    const unavailable = isUnavailableDay(currentDate)

    // Check edges:
    const firstReservation = isFirstReservationDay(currentDate)
    const lastReservation = isLastReservationDay(currentDate)
    const firstMain = isFirstMainEventDay(currentDate)
    const lastMain = isLastMainEventDay(currentDate)
    const firstAvail = isFirstAvailableDay(currentDate)
    const lastAvail = isLastAvailableDay(currentDate)
    const firstUnavail = isFirstUnavailableDay(currentDate)
    const lastUnavail = isLastUnavailableDay(currentDate)

    const firstSelected = isFirstSelectedDay(currentDate)
    const lastSelected = isLastSelectedDay(currentDate)

    // Start with a base day style
    const classNames = [styles.day]

    // If from previous/next month, or if it’s a disabled day
    if (isOtherMonth) classNames.push(styles.otherMonthDay)
    if (isDisabled) classNames.push(styles.disabled)

    // If the day is in the user’s currently selected or hovered range
    if (inSelectedRange || inHoveredRange) classNames.push(styles.selectedDateRange)

    // If only a start day is selected (no end yet) & currentDate == start date
    if (!hasFullSelectedRange && selectedRange?.start && !selectedRange.end) {
      const sameDay = normalizeDate(selectedRange.start).getTime() === normalizeDate(currentDate).getTime()
      if (sameDay) classNames.push(styles.selectedDateRange)
    }

    // Add classes for day "type" blocks, if not in viewOnly mode
    if (resDay) classNames.push(styles.reservationRange)
    if (!viewOnly && mainEvent) classNames.push(styles.mainEventRange)
    if (!viewOnly && available) classNames.push(styles.disponibilityRange)
    if (!viewOnly && unavailable) classNames.push(styles.indisponibilityRange)

    // If we’re in viewOnly mode and day is mainEvent or available, we highlight
    if (viewOnly && (mainEvent || available)) classNames.push(styles.viewOnlyRange)

    // Mark edges for visual “round corners” or styling
    if (firstSelected || lastSelected) classNames.push(styles.selected)
    if (firstReservation || firstMain || firstAvail || firstUnavail) classNames.push(styles.firstRangeDay)
    if (lastReservation || lastMain || lastAvail || lastUnavail) classNames.push(styles.lastRangeDay)

    return classNames.join(' ')
  }

  /**
   * Renders all 42 day cells:
   *  - Some from the previous month
   *  - The current month
   *  - Some from the next month
   */
  const renderDays = (): JSX.Element[] => {
    const result: JSX.Element[] = []

    // 1) "Prev month" days (the blank cells before day=1)
    for (let i = firstDayOffset - 1; i >= 0; i--) {
      const currentDate = new Date(year, month - 1, daysInPrevMonth - i)
      const classNames = buildClassNames(currentDate, true)

      // Find if we have a reservation day with a count
      const resDay = getReservationDay(currentDate)
      const reservationCount = resDay?.count ?? 0

      result.push(
        <div
          key={`prev-${i}`}
          className={classNames}
          onClick={() => !viewOnly && !isDisabledDate(currentDate) && handleDateClick?.(daysInPrevMonth - i, -1)}
          onMouseEnter={() => handleDateHover?.(currentDate)}
          onMouseLeave={() => handleDateHover?.(null)}
        >
          {daysInPrevMonth - i}
          {reservationCount >= 2 && <span className={styles.reservationCounter}>{reservationCount}</span>}
        </div>,
      )
    }

    // 2) "Current month" days
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i)
      const classNames = buildClassNames(currentDate, false)

      // Check how many reservations on this date
      const resDay = getReservationDay(currentDate)
      const reservationCount = resDay?.count ?? 0

      result.push(
        <div
          key={i}
          className={classNames}
          onClick={() => !viewOnly && !isDisabledDate(currentDate) && handleDateClick?.(i, 0)}
          onMouseEnter={() => handleDateHover?.(currentDate)}
          onMouseLeave={() => handleDateHover?.(null)}
        >
          {i}
          {reservationCount >= 2 && <span className={styles.reservationCounter}>{reservationCount}</span>}
        </div>,
      )
    }

    // 3) "Next month" days (fill up to 6 rows)
    const totalCells = 42 // 6 weeks * 7 days
    const neededCells = totalCells - result.length
    for (let i = 1; i <= neededCells; i++) {
      const currentDate = new Date(year, month + 1, i)
      const classNames = buildClassNames(currentDate, true)

      // If you want the same logic for reservations:
      const resDay = getReservationDay(currentDate)
      const reservationCount = resDay?.count ?? 0

      result.push(
        <div
          key={`next-${i}`}
          className={classNames}
          onClick={() => !viewOnly && !isDisabledDate(currentDate) && handleDateClick?.(i, 1)}
          onMouseEnter={() => handleDateHover?.(currentDate)}
          onMouseLeave={() => handleDateHover?.(null)}
        >
          {i}
          {reservationCount >= 2 && <span className={styles.reservationCounter}>{reservationCount}</span>}
        </div>,
      )
    }

    return result
  }

  /** Final render. */
  return (
    <div className={styles.calendar}>
      {/* Calendar Header */}
      <div className={styles.header}>
        <button type="button" className={styles.button} onClick={() => shiftMonth(-1)}>
          <Up />
        </button>
        {monthYearPicker ? (
          <p className={styles.title}>
            <button type="button" className={styles.titleButton} onClick={() => toggleView('months')}>
              {formatMonth(month)}
            </button>
            <button type="button" className={styles.titleButton} onClick={() => toggleView('years')}>
              {year}
            </button>
          </p>
        ) : (
          <p className={styles.title}>
            {formatMonth(month)} {year}
          </p>
        )}
        <button type="button" className={`${styles.button} ${styles.down}`} onClick={() => shiftMonth(1)}>
          <Up />
        </button>
      </div>

      <div className={styles.separator} />

      {view === 'months' && (
        <div ref={pickerListRef} className={styles.pickerList}>
          {[...Array(12)].map((_, m) => (
            <button
              type="button"
              key={m}
              className={`${styles.pickerItem} ${m === month ? styles.selected : ''}`}
              data-selected={m === month}
              disabled={isBeforeMin(new Date(year, m + 1, 0)) || isAfterMax(new Date(year, m, 1))}
              onClick={() => {
                setCurrentMonth(new Date(year, m, 1))
                setView('days')
              }}
            >
              {formatMonth(m)}
            </button>
          ))}
        </div>
      )}

      {view === 'years' && (
        <div ref={pickerListRef} className={styles.pickerList}>
          {[...Array(lastYear - firstYear + 1)].map((_, i) => {
            const y = firstYear + i
            return (
              <button
                type="button"
                key={y}
                className={`${styles.pickerItem} ${y === year ? styles.selected : ''}`}
                data-selected={y === year}
                onClick={() => {
                  setCurrentMonth(new Date(y, month, 1))
                  setView('days')
                }}
              >
                {y}
              </button>
            )
          })}
        </div>
      )}

      {view === 'days' && (
        <>
          {/* Days-of-week row(s) */}
          <div className={`${styles.daysOfWeek} ${weeksPerRow === 2 ? styles.twoWeeksGrid : ''}`}>
            {[...Array(weeksPerRow)].flatMap((_, weekIndex) =>
              daysOfWeek.map((day, dayIndex) => {
                const uniqueKey = `${day}-${weekIndex}-${dayIndex}`
                return (
                  <div key={uniqueKey} className={styles.dayName}>
                    {day}
                  </div>
                )
              }),
            )}
          </div>

          {/* 42 day cells */}
          <div className={`${styles.daysGrid} ${weeksPerRow === 2 ? styles.twoWeeksGrid : ''}`}>{renderDays()}</div>
        </>
      )}
    </div>
  )
}
